import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts';
import { aggregateWeeklyTSS } from '@/lib/analytics-calculations';

interface WeeklyTSSChartProps {
  dates: string[];
  dailyTss: number[];
  weeklyDates?: string[];
  weeklyTss?: number[];
}

export function WeeklyTSSChart({ dates, dailyTss, weeklyDates, weeklyTss }: WeeklyTSSChartProps) {
  const data = useMemo(() => {
    const weeks = Array.isArray(weeklyDates) && Array.isArray(weeklyTss) && weeklyDates.length === weeklyTss.length
      ? weeklyDates.map((weekStart, i) => ({
          weekStart,
          tss: weeklyTss[i] ?? 0,
          avgDaily: (weeklyTss[i] ?? 0) / 7,
        }))
      : aggregateWeeklyTSS(dates, dailyTss);
    return weeks.map((w, i) => {
      const slice = weeks.slice(Math.max(0, i - 3), i + 1);
      const avg = slice.reduce((s, v) => s + v.tss, 0) / slice.length;
      return { ...w, weekLabel: w.weekStart.slice(5), rollingAvg: Math.round(avg) };
    });
  }, [dates, dailyTss, weeklyDates, weeklyTss]);

  if (!data.length) return null;

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Weekly Training Stress</h3>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="weekLabel" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={35} />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
            <Bar dataKey="tss" fill="hsl(var(--chart-1))" radius={[3, 3, 0, 0]} name="Weekly TSS" />
            <Line dataKey="rollingAvg" stroke="hsl(var(--chart-4))" strokeWidth={1.5} dot={false} name="4-Week Avg" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
